// A Fooldal.tsx dashboard-kártyáinak (Heti átlag, terápia hete) számításai
// külön, tesztelhető függvényekben — 2026-08-14 éles hiba után (a "Heti
// átlag" kártya valójában az utolsó két napló-bejegyzés nyers különbségét
// mutatta, "/ hét" felirattal, a naplózási gyakoriságtól függő, félrevezető
// értéket adva, nem egy időarányos rátát).

/** Hányadik hete tart a terápia (1-indexelt — az indulás napja az 1. hét része). */
export function calcTherapyWeek(startDate: Date, now: Date = new Date()): number {
  const diffMs = now.getTime() - startDate.getTime();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (7 * 24 * 60 * 60 * 1000));
}

/**
 * Teljes terápiára vetített heti átlag fogyás (kg/hét) — összes fogyás
 * osztva az eltelt hetekkel. Szándékosan NEM az utolsó két bejegyzés
 * közti különbség: az a naplózási gyakoriságtól függ, nem egy
 * összehasonlítható, időarányos rátát ad.
 *
 * Hízásnál vagy elégtelen adatnál (0 eltelt hét, nincs fogyás) null-t ad
 * vissza — a hívó felelőssége, hogy ilyenkor "–"-t jelenítsen meg
 * NaN/Infinity helyett.
 */
export function calcWeeklyAverageLossKg(
  totalLossKg: number | null | undefined,
  weeksElapsed: number | null | undefined
): number | null {
  if (!totalLossKg || totalLossKg <= 0) return null;
  if (!weeksElapsed || weeksElapsed <= 0) return null;
  return totalLossKg / weeksElapsed;
}

/**
 * Injekciós ciklus hányadik napja (0-indexelt), `intervalDays`-ig — az
 * utolsó nap az injekció napja, utána is ott marad, amíg a user nem rögzít
 * új dózist. A cap korábban fixen 7 volt (a Fooldal.tsx PHASES tömbjének
 * 7. napi eleme — "Alacsony fázis" — enélkül örökre elérhetetlen lett
 * volna); #86cb5dbkn óta a cap a beállított ciklushosszra (`intervalDays`,
 * default 7) igazodik, mert 6 napos/mikrodózisos ütemű felhasználók is
 * vannak.
 */
export function calcCycleDays(lastInjDate: Date, intervalDays: number = 7, now: Date = new Date()): number {
  return calcCycleState(lastInjDate, intervalDays, now).cycleDays;
}

/**
 * A ciklus TELJES állapota — a kanonikus forrás (CLAUDE.md 10. szabály).
 *
 * ⚠️ Miért kellett ez (2026-09-29, ClickUp 123qmzqympy): a Főoldal hero-kártyája
 * egyszerre mondta, hogy az injekció „Lejárt", ÉS hogy „Ma van az injekció!".
 * Ugyanabból a `lastInjDate`-ből két külön számítás futott:
 *
 *   daysUntil = ceil((nextInjDate − now) / nap)   → lineáris, negatívba megy
 *   cycleDays = calcCycleDays(...)                 → intervalDays-re VÁG
 *
 * A vágás miatt egy 16 napja elmaradt injekció `cycleDays = 7`-et adott, ami
 * pont az utolsó fázis („Alacsony fázis — Injekció napja") tartománya. A user
 * tartósan a „ma van az injekció" üzeneten ragadt, miközben fölötte a „Lejárt"
 * állt.
 *
 * A megoldás nem a vágás megszüntetése — arra a fázis-kiválasztásnak szüksége
 * van —, hanem hogy a HÍVÓ IS MEGTUDJA, hogy vágás történt.
 *
 * ⚠️ A `daysUntil` SZÁNDÉKOSAN innen jön, nem külön számításból. A kettő
 * algebrailag azonos:
 *
 *   daysUntil = ceil(N − x) = N − floor(x) = intervalDays − rawDays
 *   (x = eltelt napok törtszámmal, N = intervalDays)
 *
 * Ha külön maradna, a két érték megint el tudna csúszni egymástól — pontosan
 * ez a hibaosztály ütött már a „hét" kétféle számításánál és a bázissúlynál is.
 */
export interface CycleState {
  /** A ciklus napja, `intervalDays`-re vágva — a fázis-kiválasztáshoz. */
  cycleDays: number;
  /** A nyers, VÁGATLAN napszám az utolsó injekció óta (sosem negatív). */
  rawDays: number;
  /** Hátralévő napok a következő injekcióig; elmaradásnál NEGATÍV. */
  daysUntil: number;
  /** Lejárt-e a ciklus: a nyers napszám MEGHALADJA az intervallumot. */
  isOverdue: boolean;
  /** Hány nappal járt le (0, ha nem járt le). */
  overdueDays: number;
}

export function calcCycleState(
  lastInjDate: Date,
  intervalDays: number = 7,
  now: Date = new Date()
): CycleState {
  const rawDays = Math.max(
    0,
    Math.floor((now.getTime() - lastInjDate.getTime()) / (24 * 60 * 60 * 1000))
  );
  const cycleDays = Math.min(intervalDays, rawDays);
  const daysUntil = intervalDays - rawDays;
  const isOverdue = rawDays > intervalDays;
  return {
    cycleDays,
    rawDays,
    daysUntil,
    isOverdue,
    overdueDays: isOverdue ? rawDays - intervalDays : 0,
  };
}

/**
 * A user által megadott horgony-dátumot (`profiles.injectionAnchorDate`,
 * ha van) a legutóbbi, `now`-ig eltelt teljes ciklusra görgeti előre — így
 * a "Következő injekció" mindig a jövőben marad anélkül, hogy a usernek
 * minden ciklusban újra kellene naplóznia. Horgony hiányában a meglévő,
 * entry-derivált dátumra esik vissza (nincs viselkedésváltozás azoknak,
 * akik nem állítják be) — #86cb5dbkn, egyben a 2026-08-20-i FB-hibajelentés
 * (rossz injekciónap, mert az utolsó dózisos bejegyzés véletlen napra esett
 * és azóta nem frissült) javítása.
 */
export function resolveInjectionAnchor(
  anchorDateStr: string | null | undefined,
  intervalDays: number,
  fallbackLastInjDate: Date | null,
  now: Date = new Date()
): Date | null {
  if (!anchorDateStr) return fallbackLastInjDate;
  const anchor = new Date(`${anchorDateStr}T00:00:00`);
  if (isNaN(anchor.getTime())) return fallbackLastInjDate;
  const diffDays = Math.floor((now.getTime() - anchor.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return anchor;
  const cyclesElapsed = Math.floor(diffDays / intervalDays);
  const rolled = new Date(anchor);
  rolled.setDate(rolled.getDate() + cyclesElapsed * intervalDays);
  return rolled;
}

// 2026-08-15: a "kezdő súly" bázis Fooldal.tsx-ben és Statisztika.tsx-ben
// szó szerint duplikált, inline logika volt — docs/FOGALMAK.md audit
// szerint pont az a hibaosztály, ami miatt ez a fájl létrejött.
//
// Fontos, a 08.14-i körben tanult különbségtétel: a profilban megadott
// kezdő súly csak akkor a kanonikus bázis, ha a nézet a TELJES terápiára
// vonatkozik (pl. "Összes" időszak-fül, vagy egy időszak-fogalom nélküli
// felület, mint a PDF-riport). Egy szűkebb, IDŐSZAK-RELATÍV nézetben
// ("2 hét", "1 hónap") a helyes bázis az adott időszak eleji bejegyzés
// súlya, NEM a lifetime kezdő súly — a Statisztika oldal korábban ezt
// összekeverte, ami a szűk időszak-nézeteket is lifetime-számmal
// mutatta volna.

/**
 * Van-e a bejegyzésnek HASZNÁLHATÓ (szám) súlya.
 *
 * ⚠️ Miért nem `weight !== undefined` (2026-09-16): a D2 döntés óta
 * (súly-kötelezőség feloldása) a kliens a hiányzó súly helyére `null`-t ír
 * (`normalizeWeightForWrite`), és a Firestore-szabály ezt kifejezetten
 * engedi (`data.weight == null`). A `null !== undefined` viszont IGAZ,
 * tehát a régi szűrő ÁTENGEDTE a súly nélküli bejegyzést. Következmény,
 * amíg ez így volt: ha a legutolsó bejegyzés dózis-only, a "Jelenlegi
 * súly" és az "Összes fogyás" gondolatjelre vált, a célsúly-sáv eltűnik,
 * és az AI is súly nélkülinek látja a felhasználót — hiába van előtte
 * bármennyi mért bejegyzés. Épp az a használati minta törik el tőle,
 * amiért a D2 döntés megszületett.
 *
 * A `weight` háromféle lehet a drótról: szám (mért), `null` (a mai
 * kliens), `undefined` (régi dokumentum, amiben nincs is ilyen mező).
 * Ez a függvény mindhármat egy helyen dönti el.
 */
export function hasUsableWeight(entry: { weight?: number | null }): boolean {
  return typeof entry.weight === "number" && Number.isFinite(entry.weight);
}

/**
 * A súllyal rendelkező bejegyzések, az eredeti sorrendben.
 *
 * Minden súly-idősor (grafikon, első/utolsó súly, trial-százalék, bázis)
 * ezen menjen át — a szűretlen listából vett `[0]` / `[last]` azóta hazudik,
 * hogy létezhet súly nélküli bejegyzés.
 */
export function selectWeighedEntries<T extends { weight?: number | null }>(
  entries: T[]
): T[] {
  return entries.filter(hasUsableWeight);
}

/**
 * A HASZNÁLHATÓ (véges szám) vércukor-értéket tartalmazó bejegyzések, az
 * eredeti sorrendben.
 *
 * ⚠️ B3 (külső audit, 2026-10-14): a vércukor-idősor NEM a
 * `selectWeighedEntries` kimenetéből épülhet — a D2 óta a csak vércukrot
 * tartalmazó bejegyzés megengedett, és a súly-szűrő kiejtette a grafikonról.
 */
export function selectGlucoseEntries<T extends { glucose?: number | null }>(
  entries: T[]
): T[] {
  return entries.filter(
    (e) => typeof e.glucose === "number" && Number.isFinite(e.glucose)
  );
}

/**
 * A kezdő súly bázisa. `isFullHistory=true`-nál a profil kezdő súlya a
 * kanonikus forrás (fallback: az időszak első bejegyzésének súlya, ha a
 * profil-mező nincs kitöltve). `isFullHistory=false`-nál (időszak-relatív
 * nézet) mindig az időszak első bejegyzésének súlya, a profil-mezőtől
 * függetlenül.
 *
 * ⚠️ A `periodFirstWeight` mindig az első SÚLLYAL rendelkező bejegyzésből
 * jöjjön (`selectWeighedEntries`), ne a lista első eleméből.
 */
export function resolveBaseWeight(
  profilKezdoSuly: string | null | undefined,
  periodFirstWeight: number | null | undefined,
  isFullHistory: boolean
): number | undefined {
  if (isFullHistory) {
    const parsed = profilKezdoSuly ? parseFloat(profilKezdoSuly) : NaN;
    return Number.isFinite(parsed) ? parsed : (periodFirstWeight ?? undefined);
  }
  return periodFirstWeight ?? undefined;
}

/** Összes fogyás (kg) a bázissúly és a jelenlegi súly különbségeként. */
export function calcTotalLoss(
  baseWeight: number | null | undefined,
  lastWeight: number | null | undefined
): number | null {
  if (!baseWeight || !lastWeight) return null;
  return baseWeight - lastWeight;
}

/** Összes fogyás százalékban, a bázissúlyhoz viszonyítva. */
export function calcTotalLossPct(
  baseWeight: number | null | undefined,
  totalLoss: number | null | undefined
): number | null {
  if (!baseWeight || !totalLoss) return null;
  return (totalLoss / baseWeight) * 100;
}

/**
 * Egy időszak összegzése (Főoldal „Kezdő / Jelenlegi / Fogyás" kártya).
 *
 * ⚠️ A7 (külső audit, 2026-10-14): a hívó korábban a SZŰRETLEN listából
 * vette az első és utolsó súlyt. Súly nélküli utolsó bejegyzésnél (D2) a
 * kártya „Jelenlegi: kg"-ot és „–" fogyást mutatott, miközben a fenti
 * „Jelenlegi súly" kártya a helyes értéket. A súly-szűrés ezért itt, a
 * függvényen BELÜL történik, hogy a hívó ne felejthesse el.
 */
export function calcPeriodSummary<T extends { weight?: number | null }>(
  periodEntries: T[],
  profilKezdoSuly: string | null | undefined,
  isFullHistory: boolean
): { baseWeight: number | undefined; lastWeight: number | undefined; loss: number | null; lossPct: number | null } {
  const weighed = selectWeighedEntries(periodEntries);
  const baseWeight = resolveBaseWeight(profilKezdoSuly, weighed[0]?.weight, isFullHistory);
  const lastWeight = weighed[weighed.length - 1]?.weight ?? undefined;
  const loss = calcTotalLoss(baseWeight, lastWeight);
  return { baseWeight, lastWeight, loss, lossPct: calcTotalLossPct(baseWeight, loss) };
}

/**
 * A terápia kezdete: a profilban megadott dátum, ennek hiányában az első
 * (dátum szerint rendezett) bejegyzés dátuma. A Főoldal és az AI-kontextus
 * ugyanezt használja (A7, 2026-10-14) — korábban az AI kezdődátum nélkül
 * `null` hetet kapott, miközben a Főoldal „N. hét"-et mutatott.
 */
export function resolveTherapyStart(
  terapiaKezdete: string | null | undefined,
  entriesByDateAsc: { date?: string }[]
): Date | null {
  if (terapiaKezdete) return new Date(terapiaKezdete);
  const first = entriesByDateAsc[0]?.date;
  return first ? new Date(first) : null;
}

/**
 * A súlyváltozás megjelenített szövege a FOGYÁS (kg) előjeléből.
 *
 * ⚠️ B2 (külső audit, 2026-10-14): a hívók korábban `-${loss}`-t írtak ki,
 * ami súlynövekedésnél „--2.0 kg"-ot adott. FPM döntése: növekedésnél
 * „+2.0 kg", SEMLEGES színnel — az `isGain` ehhez kell a hívónak (2. szabály:
 * mért egészségügyi értéknél a szín is üzenet). A 0.05 alatti változás
 * „0.0 kg", hogy ne jelenjen meg „-0.0".
 */
export function formatWeightChange(
  lossKg: number | null | undefined
): { text: string; isGain: boolean } {
  if (lossKg == null || !Number.isFinite(lossKg)) return { text: "–", isGain: false };
  const abs = Math.abs(lossKg).toFixed(1);
  if (abs === "0.0") return { text: "0.0 kg", isGain: false };
  return lossKg > 0 ? { text: `-${abs} kg`, isGain: false } : { text: `+${abs} kg`, isGain: true };
}

/** A súlyváltozás százaléka zárójelben — fogyásnál előjel nélkül (a meglévő
 * megjelenés), növekedésnél „+" előjellel. */
export function formatWeightChangePct(lossPct: number | null | undefined): string | null {
  if (lossPct == null || !Number.isFinite(lossPct)) return null;
  return lossPct < 0 ? `(+${Math.abs(lossPct).toFixed(1)}%)` : `(${lossPct.toFixed(1)}%)`;
}

/** Célsúly-haladás %-ban: 0 és 100 közé szorítva (B2 — növekedésnél korábban
 * negatív lett, és a sáv `width: -5%`-ot kapott). Értelmezhetetlen célnál null. */
export function calcGoalPct(
  totalLoss: number | null | undefined,
  totalTarget: number | null | undefined
): number | null {
  if (totalLoss == null || !totalTarget || totalTarget <= 0) return null;
  return Math.min(100, Math.max(0, Math.round((totalLoss / totalTarget) * 100)));
}

/** A súlyváltozás-kártya felirata: növekedésnél „Változás” (FPM döntése,
 * 2026-10-14 — ne „Fogyás: +2.0 kg” álljon), egyébként a megszokott „Fogyás”. */
export function weightChangeLabel(
  lossKg: number | null | undefined,
  scope: "total" | "period"
): string {
  const isGain = formatWeightChange(lossKg).isGain;
  if (scope === "total") return isGain ? "Összes változás" : "Összes fogyás";
  return isGain ? "Változás" : "Fogyás";
}
